import Tag from "@amzn/meridian/tag";
import { allFilters } from "../../features/filter-sheets/definitions";
import _, {
    concat,
    defaultTo,
    get,
    hasIn,
    inRange,
    kebabCase,
    keys,
    lowerCase,
    omit,
    pick,
    uniq,
    values,
} from "lodash";
import moment from "moment";
import { json2csv } from "json-2-csv";
import {
    additionalColumnsPrefers,
    additionalTableHeaderCells,
    statusOrderMap,
    tableMapping,
} from "./definitions";
import { DEFAULT_DATE_FORMAT } from "../../utils/formattingUtils";
import { AuditItem } from "../../endpoints/auditEventTrigger";

export const getCurrencyFormat = (amount: any) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
};

export const handleSort = (tableDate: any[], sortDirection: string, sortColumn: string) => {
    return tableDate?.sort((a, b) => {
        let valueA = defaultTo(a[sortColumn], "");
        let valueB = defaultTo(b[sortColumn], "");

        if (sortColumn.toLocaleLowerCase().includes("date")) {
            valueA = moment(defaultTo(a[sortColumn], "01-01-0001")).unix();
            valueB = moment(defaultTo(b[sortColumn], "01-01-0001")).unix();
        }

        if (valueA < valueB) {
            return sortDirection === "ascending" ? -1 : 1;
        }
        if (valueA > valueB) {
            return sortDirection === "ascending" ? 1 : -1;
        }

        return 0;
    });
};

export const filterData = (filterMap: Map<string, any>, filterData: any[]) => {
    if (filterMap.size === 0) {
        return filterData;
    }

    return filterData.filter(data => {
        const filterKeysArray = [...filterMap.keys()];
        const results = filterKeysArray.map(key => {
            switch (allFilters[key].type) {
                case "INPUT": {
                    if (key === "address") {
                        return lowerCase(joinedAddress(data[key])).includes(
                            lowerCase(filterMap.get(key)?.value)
                        );
                    }
                    if (key === "keyword") {
                        return lowerCase(
                            data["mailId"] ||
                                data["parcelNumber"] ||
                                data["propertyNumber"] ||
                                data["fileName"]
                        ).includes(lowerCase(filterMap.get(key)?.value));
                    }

                    return lowerCase(data[key]).includes(lowerCase(filterMap.get(key)?.value));
                }
                case "SELECT": {
                    return data[key] === filterMap.get(key).value;
                }
                case "SELECT_SEARCH": {
                    if (key === "state" || key === "jurisdiction") {
                        return hasIn(data, "address.state") || hasIn(data, "address.jurisdiction")
                            ? kebabCase(data.address[key]).includes(
                                  kebabCase(filterMap.get(key).value)
                              )
                            : false;
                    }

                    return data[key] === filterMap.get(key).value;
                }
                case "DATE_RANGE": {
                    const filterStartDate = new Date(
                        filterMap.get(key).value[0]?.trim() || "0000-01-01"
                    );
                    const filterEndDate = new Date(
                        filterMap.get(key).value[1]?.trim() || "9999-12-31"
                    );
                    const dataDate = new Date(data[key]);

                    return dataDate >= filterStartDate && dataDate <= filterEndDate;
                }
                case "INPUT_RANGE": {
                    const startingNumber = Number(filterMap.get(key).value[0]?.trim() || 0);
                    const endingNumber =
                        Number(filterMap.get(key).value[1]?.trim() || Math.pow(10, 1000)) + 1;
                    const value = Number(data[key]);

                    return inRange(value, startingNumber, endingNumber);
                }
            }
        });

        return results.every(t => t === true);
    });
};

export const formatStatus = (status: string) => {
    switch (status) {
        case "OPEN": {
            return <Tag type={"success"}>Open</Tag>;
        }
        case "SUCCEEDED": {
            return <Tag type={"success"}>Succeeded</Tag>;
        }
        case "ACTIVE": {
            return <Tag type={"success"}>Active</Tag>;
        }
        case "INACTIVE": {
            return <Tag type={"neutral"}>Inactive</Tag>;
        }
        case "CLOSED": {
            return <Tag type={"neutral"}>Closed</Tag>;
        }
        case "OVERDUE": {
            return <Tag type={"error"}>Overdue</Tag>;
        }
        case "UNREAD": {
            return <Tag type={"theme"}>Unread</Tag>;
        }
        case "FAILED": {
            return <Tag type={"error"}>Error</Tag>;
        }
        case "PARTIALLY_SUCCEEDED": {
            return <Tag type={"warning"}>Partially Succeeded</Tag>;
        }
        case "IN_PROGRESS": {
            return <Tag type={"theme"}>In Progress</Tag>;
        }
        case "NEW": {
            return <Tag type={"error"}>New</Tag>;
        }
        case "PREPARING": {
            return <Tag type={"theme"}>Preparing</Tag>;
        }
        case "EDITING": {
            return <Tag type={"theme"}>Editing</Tag>;
        }
        case "UNDER_APPEAL": {
            return <Tag type={"warning"}>Under appeal</Tag>;
        }
        case "COMPLETE": {
            return <Tag type={"success"}>Complete</Tag>;
        }
        case "NOT_APPEALED": {
            return <Tag type={"neutral"}>Not appealed</Tag>;
        }
        case "DELETED": {
            return <Tag type={"neutral"}>Deleted</Tag>;
        }
    }
};

export const joinedAddress = (address: string) => {
    return values(pick(address, ["streetAddress", "city", "state", "country"]))
        .filter(value => value.trim().length > 0)
        .join(", ");
};

export const exportCurrentTable = (tableData: any[], currentTable: string) => {
    if (tableData.length <= 0) {
        return;
    }

    const valuesToOmit = ["address", "documentS3PreSignedUrl", "association"];
    const valuesToPushToEnd = [
        "canopyId",
        "createdByUser",
        "documentType",
        "artifactId",
        "artifactType",
        "treasureStatus",
    ];

    const getFormattedData = (data: { address: any }) => {
        const dataWithOmittedValues = omit(data, valuesToOmit);

        return { ...dataWithOmittedValues, ...data.address };
    };
    const dataHeaders = uniq(
        concat(
            ...tableData.map(data => {
                return keys(getFormattedData(data));
            })
        )
    );

    const orderedHeaders = [
        ...dataHeaders.filter((value: string) => !valuesToPushToEnd.includes(value)).sort(),
        ...dataHeaders.filter((value: string) => valuesToPushToEnd.includes(value)).sort(),
    ];

    const dataValues = tableData.map(data => {
        const formattedData = getFormattedData(data);
        const returnValue: Array<any> = [];
        orderedHeaders.forEach(header => {
            returnValue.push(get(formattedData, header, "-"));
        });

        return returnValue;
    });

    const data = [[...orderedHeaders], ...dataValues];
    const file = new File(
        [
            json2csv(data, {
                emptyFieldValue: "-",
                escapeHeaderNestedDots: false,
                arrayIndexesAsKeys: false,
            }),
        ],
        kebabCase(currentTable + moment().unix().toString()) + ".csv"
    );
    downloadBlob(file, file.name);
};

export const downloadBlob = (blob: any, filename: string) => {
    const url = URL.createObjectURL(blob);
    const anchorElement = document.createElement("a");
    anchorElement.href = url;
    anchorElement.download = filename || "download";
    const clickHandler = () => {
        setTimeout(() => {
            URL.revokeObjectURL(url);
            anchorElement.removeEventListener("click", clickHandler);
        }, 150);
    };
    anchorElement.addEventListener("click", clickHandler, false);
    anchorElement.click();

    return anchorElement;
};

export const getTableCells = (
    headerCells: any[],
    dataMap: Map<string, any>,
    currentTable: string
) => {
    return headerCells.map(cell => {
        if (dataMap.has(cell.id)) {
            switch (cell.id) {
                case "address": {
                    if (cell.subId) {
                        return {
                            value: dataMap.get(cell.id)[cell.subId] ?? "-",
                            id: cell.subId,
                            type: cell.displayType,
                        };
                    }
                    const address = dataMap.get(cell.id);

                    return {
                        value: joinedAddress(address) ?? "-",
                        id: cell.id,
                    };
                }
                case "mailId": {
                    return {
                        value: dataMap.get(cell.id),
                        id: cell.id,
                        link: `/${currentTable}/${dataMap.get("artifactId")}`,
                        type: cell.displayType,
                    };
                }
                case "parcelNumber": {
                    if (dataMap.get("artifactType") === "PARCEL") {
                        return {
                            value: dataMap.get(cell.id),
                            id: cell.id,
                            link: `/parcel-details/${dataMap.get("artifactId")}`,
                            type: cell.displayType,
                        };
                    }

                    if (Object.keys(dataMap.get("association") || {})?.length !== 0) {
                        return {
                            value: dataMap.get(cell.id),
                            id: cell.id,
                            link: `/parcel-details/${dataMap.get("association")?.documentAssociation[0]?.parcelArtifactId}`,
                            type: cell.displayType,
                        };
                    }

                    return {
                        value: dataMap.get(cell.id),
                        id: cell.id,
                        link: "",
                        type: cell.displayType,
                    };
                }
                case "propertyNumber": {
                    return {
                        value: dataMap.get(cell.id),
                        id: cell.id,
                        link: `/property-details/${dataMap.get("artifactId")}`,
                        type: cell.displayType,
                    };
                }
                case "accountNumber": {
                    if (Object.keys(dataMap.get("association") || {})?.length !== 0) {
                        return {
                            value: dataMap.get(cell.id),
                            id: cell.id,
                            link: `/parcel-details/${dataMap.get("association")?.documentAssociation[0]?.parcelArtifactId}`,
                            type: cell.displayType,
                        };
                    }

                    return {
                        value: dataMap.get(cell.id) ?? "-",
                        id: cell.id,
                        link: "",
                        type: cell.displayType,
                    };
                }
                case "siteCode": {
                    return {
                        value: dataMap.get(cell.id),
                        id: cell.id,
                        link: `/property-details/${dataMap.get("appealAssociation") ? dataMap.get("appealAssociation")?.associatedPropertyArtifactId : dataMap.get("artifactId")}`,
                        type: cell.displayType,
                    };
                }
                default: {
                    if (currentTable === "audit-records-mails") {
                        return {
                            value: dataMap.get(cell.id),
                            id: cell.id,
                            type: cell.displayType,
                            s3Prefix: "public/",
                        };
                    } else if (currentTable === "audit-records-data-source-files") {
                        return {
                            value: dataMap.get(cell.id),
                            id: cell.id,
                            type: cell.displayType,
                            s3Prefix: `APPRAISER-AI/DATA-SOURCE-UPLOAD/${dataMap.get("country")}/${dataMap.get("dataSourceType")}/${dataMap.get("dataType")}/`,
                        };
                    } else {
                        return {
                            value: dataMap.get(cell.id),
                            id: cell.id,
                            type: cell.displayType,
                            s3Prefix: "public/",
                        };
                    }
                }
            }
        }

        return { value: "-" };
    });
};

export const handleTableDataFormatting = (
    data: {
        artifactDetailsMap: any;
        artifactId: string;
        artifactType: string;
        treasureStatus: string;
        updatedOn: string;
    }[]
) => {
    return [
        ...data.map(
            (details: {
                artifactDetailsMap: any;
                artifactId: string;
                artifactType: string;
                treasureStatus: string;
                updatedOn: string;
            }) => {
                return {
                    ...details.artifactDetailsMap?.propertyDetails,
                    ...details.artifactDetailsMap,
                    ...details.artifactDetailsMap?.association,
                    ...details.artifactDetailsMap?.appealApproachCalculationData?.summary
                        ?.indicatedValueSummary,
                    ...details.artifactDetailsMap?.appealApproachCalculationData?.summary
                        ?.assessment,
                    artifactId: details.artifactId,
                    artifactType: details.artifactType,
                    treasureStatus: details.treasureStatus,
                    updatedOn: moment(details.updatedOn).format(DEFAULT_DATE_FORMAT),
                };
            }
        ),
    ].sort((statusA, statusB) => {
        return statusOrderMap[statusA?.status] - statusOrderMap[statusB?.status];
    });
};

export const handleAuditRecordsFormatting = (data: Array<AuditItem>) => {
    return [
        ...data.map(record => {
            return record.contentMap;
        }),
    ];
};

export const getDefaultTablePreferences = () => {
    const allTablePreferences: Map<string, any> = new Map<string, any>();

    keys(tableMapping).map((tableKey: string) => {
        let uniqueColumns = new Map();
        uniqueColumns = getUniqueColumns(uniqueColumns, tableMapping[tableKey].tableHeader, true);
        uniqueColumns = getUniqueColumns(uniqueColumns, additionalColumnsPrefers, false);

        return allTablePreferences.set(tableKey, Array.from(uniqueColumns.values()));
    });

    return {
        showDividers: false,
        showStripes: false,
        highlightOnHover: false,
        tableSpacing: "small",
        allTables: [Object.fromEntries(allTablePreferences)][0],
    };
};

export const getUniqueColumns = (columnsMap: Map<any, any>, columns: any[], isVisible: boolean) => {
    const uniqueColumns = columnsMap;

    columns.map(column => {
        if (column.subId) {
            !uniqueColumns.has(column.subId)
                ? uniqueColumns.set(column.subId, {
                      id: column.id,
                      subId: column.subId,
                      visible: isVisible,
                  })
                : null;

            return;
        }

        !uniqueColumns.has(column.id)
            ? uniqueColumns.set(column.id, {
                  id: column.id,
                  visible: isVisible,
              })
            : null;

        return;
    });

    return uniqueColumns;
};

export const getTableHeaders = (
    preferences: {
        showDividers?: boolean;
        showStripes?: boolean;
        highlightOnHover?: boolean;
        tableSpacing?: string;
        allTables: any;
    },
    tableType: string
) => {
    const tableColumnsPreferences = new Map(Object.entries(preferences.allTables)).get(tableType);

    const visibleColumns = _.filter(tableColumnsPreferences, _.matches({ visible: true }));

    const intersection = (allPossibleColumns: any, visibleColumns: any[]) => {
        return _.pull(
            visibleColumns.map(column => {
                if (column.subId) {
                    return _.find(allPossibleColumns, ["subId", column.subId]);
                }

                return _.find(allPossibleColumns, ["id", column.id]);
            }),
            undefined
        );
    };

    return intersection(getAllPossibleColumns(tableType), visibleColumns).sort(
        (a, b) => a?.order - b?.order
    );
};

export const getAllPossibleColumns = (tableName: string) => {
    if (tableMapping[tableName]?.displayAdditionCells === false) {
        return tableMapping[tableName]?.tableHeader;
    }

    return [...(tableMapping[tableName]?.tableHeader ?? []), ...additionalTableHeaderCells];
};

export const handleReload = (useLazyTrigger: any, request: any) => {
    useLazyTrigger(request);
};
